"use client";

import { useEffect } from "react";

/**
 * Replaces the jQuery `main.js` glue for the /groups layout with a single,
 * dependency-free React effect. It wires the genuinely-interactive template
 * behaviours to the existing server-rendered markup:
 *
 *  - Sticky header: toggles `.sticky` on `.sticky-wrapper` past 500px.
 *  - Mobile off-canvas menu: `.th-menu-toggle` buttons toggle `.th-body-visible`
 *    on `.th-menu-wrapper`; clicking the backdrop or a menu link closes it.
 *
 * (Static `data-bg-src` / shape-mockup hydration that `main.js` also did is now
 * inline styles in the components, so it needs no JS.)
 */
export default function GroupsScripts() {
  useEffect(() => {
    const cleanups: Array<() => void> = [];

    // ── Sticky header ──
    const stickyWrappers = Array.from(
      document.querySelectorAll<HTMLElement>(".sticky-wrapper"),
    );

    const onScroll = () => {
      const y = window.scrollY;
      for (const el of stickyWrappers) el.classList.toggle("sticky", y > 500);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    cleanups.push(() => window.removeEventListener("scroll", onScroll));

    // ── Mobile off-canvas menu ──
    const menuWrapper = document.querySelector<HTMLElement>(".th-menu-wrapper");
    if (menuWrapper) {
      const close = () => menuWrapper.classList.remove("th-body-visible");
      const toggle = () => menuWrapper.classList.toggle("th-body-visible");

      document
        .querySelectorAll<HTMLElement>(".th-menu-toggle")
        .forEach((btn) => {
          btn.addEventListener("click", toggle);
          cleanups.push(() => btn.removeEventListener("click", toggle));
        });

      // Clicking the dimmed backdrop (the wrapper itself, not its inner panel).
      const onBackdrop = (e: MouseEvent) => {
        if (e.target === menuWrapper) close();
      };
      menuWrapper.addEventListener("click", onBackdrop);
      cleanups.push(() =>
        menuWrapper.removeEventListener("click", onBackdrop),
      );

      // Selecting a menu link closes the menu.
      menuWrapper
        .querySelectorAll<HTMLElement>(".th-mobile-menu a")
        .forEach((a) => {
          a.addEventListener("click", close);
          cleanups.push(() => a.removeEventListener("click", close));
        });
    }

    return () => cleanups.forEach((fn) => fn());
  }, []);

  return null;
}
